import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState, useRef } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { ZoomIn, ZoomOut, X } from "lucide-react";

export default function PrintCertificatePreview({
  isOpen,
  onClose,
  UploadedPdfGif,
  isPdf = false,
}) {
  const [scale, setScale] = useState(1);
  const [showPercentage, setShowPercentage] = useState(false);
  const pdfCanvasRef = useRef(null);
  const transformRef = useRef();

  useEffect(() => {
    if (pdfjsLib.GlobalWorkerOptions.workerSrc !== pdfjsWorker) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
    }
  }, []);

  const renderPdf = async (scale = 1) => {
    if (!isPdf || !UploadedPdfGif) return;

    try {
      const loadingTask = pdfjsLib.getDocument({
        url: UploadedPdfGif,
        withCredentials: false,
      });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: scale });

      pdfCanvasRef.current.height = viewport.height;
      pdfCanvasRef.current.width = viewport.width;

      await page.render({
        canvasContext: pdfCanvasRef.current.getContext("2d"),
        viewport: viewport,
      }).promise;
    } catch (error) {
      console.error("Error rendering PDF:", error);
    }
  };

  useEffect(() => {
    if (isOpen && isPdf) {
      renderPdf(scale);
    }
  }, [isOpen, isPdf, UploadedPdfGif, scale]);

  useEffect(() => {
    let timer;
    if (showPercentage) {
      timer = setTimeout(() => setShowPercentage(false), 1000);
    }
    return () => clearTimeout(timer);
  }, [showPercentage]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-lg bg-cfSecondary text-left align-middle shadow-xl transition-all">
                <div className="relative">
                  <button
                    onClick={onClose}
                    className="absolute right-4 top-2 z-10 p-2 rounded-full hover:bg-cfCardHover transition-colors focus:outline-none"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  <div className="h-[93vh] w-full relative">
                    {isPdf ? (
                      <div className="h-full w-full overflow-auto flex items-center justify-center p-4">
                        <canvas ref={pdfCanvasRef} className="shadow-lg" />
                      </div>
                    ) : (
                      <TransformWrapper
                        initialScale={1}
                        minScale={0.5}
                        maxScale={3}
                        wheel={{ step: 0.1 }}
                        onZoom={(ref) => {
                          setScale(ref.state.scale);
                          setShowPercentage(true);
                        }}
                        ref={transformRef}
                      >
                        {() => (
                          <TransformComponent
                            wrapperStyle={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                            contentStyle={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <button
                              className="border-none bg-transparent p-0 focus:outline-none"
                              onClick={() => {
                                if (scale !== 1) {
                                  transformRef.current.resetTransform();
                                  setScale(1);
                                  setShowPercentage(true);
                                }
                              }}
                            >
                              <img
                                src={UploadedPdfGif}
                                alt="Document preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            </button>
                          </TransformComponent>
                        )}
                      </TransformWrapper>
                    )}

                    {showPercentage && (
                      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black/50 text-cfSecondary px-3 py-1 rounded-md text-sm transition-opacity duration-300">
                        {Math.round(scale * 100)}%
                      </div>
                    )}

                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-4 bg-cfSecondary rounded-full p-2 shadow-lg">
                      <button
                        onClick={() => {
                          if (isPdf) {
                            setScale((prev) => Math.min(prev + 0.2, 3));
                          } else {
                            transformRef.current.zoomIn();
                          }
                          setShowPercentage(true);
                        }}
                        className="p-2 rounded-full hover:bg-cfCardHover transition-colors"
                        title="Zoom In"
                      >
                        <ZoomIn className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          if (isPdf) {
                            setScale((prev) => Math.max(prev - 0.2, 0.5));
                          } else {
                            transformRef.current.zoomOut();
                          }
                          setShowPercentage(true);
                        }}
                        className="p-2 rounded-full hover:bg-cfCardHover transition-colors"
                        title="Zoom Out"
                      >
                        <ZoomOut className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          if (isPdf) {
                            setScale(1);
                          } else {
                            transformRef.current.resetTransform();
                          }
                          setShowPercentage(true);
                        }}
                        className="p-2 rounded-full hover:bg-cfCardHover transition-colors text-sm"
                        title="Reset Zoom"
                      >
                        {Math.round(scale * 100)}%
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
